// Copyright 2022, Imprivata, Inc.  All rights reserved.
/** biome-ignore-all lint/suspicious/noExplicitAny: Any type necessary */

import { _getEnvVariable, _getUrls, initializeTracing, createSpan } from '@imprivata-cloud/common';
import type { Span } from '@opentelemetry/api';
import { SpanStatusCode } from '@opentelemetry/api';
export type TracingApi = ReturnType<typeof initializeTracing>;

import { generateWorkflowId } from '@utils/WorkflowIdHelper';

/**
 * Dynatrace attribute keys following Imprivata platform conventions
 */
export const Attributes = {
  WORKFLOW_ID: 'impr.platform.context.workflow_id' as const,
  TENANT_ID: 'impr.platform.context.tenant_id' as const,
};

/**
 * Generates base attributes for tracing spans
 * @param workflowId - The workflow ID
 * @param tenantId - The tenant ID
 * @returns Record of base attributes
 */
export function generateBaseAttributes(workflowId: string | null, tenantId?: string | null): Record<string, any> {
  const attributes: Record<string, any> = {};
  if (workflowId) {
    attributes[Attributes.WORKFLOW_ID] = workflowId;
  }
  if (tenantId) {
    attributes[Attributes.TENANT_ID] = tenantId;
  }
  return attributes;
}

export class TracingService {
  private static instance: TracingService | null = null;

  /**
   * Gets the singleton instance of TracingService
   * @returns The singleton TracingService instance
   */
  public static getInstance(): TracingService {
    if (!TracingService.instance) {
      TracingService.instance = new TracingService();
    }
    return TracingService.instance;
  }

  /**
   * Resets the singleton instance (useful for testing)
   */
  public static resetInstance(): void {
    TracingService.instance = null;
  }

  private api?: TracingApi;
  private isInitialized = false;
  private currentSpan?: Span;

  private constructor() {}

  init() {
    if (this.isInitialized) {
      return this.api;
    }

    // Configure CORS URLs for trace header propagation to backend
    const propagateTraceHeaderCorsUrls = [
      /^https:\/\/.*\.imprivata\.(com|cloud)/, // All Imprivata domains
      /^https:\/\/localhost:/, // Local development HTTPS
      /^http:\/\/localhost:/, // Local development HTTP
    ];

    this.api = initializeTracing({
      serviceName: 'papatientui',
      workflowId: generateWorkflowId(),
      OTLP_TRACING_URL: this.getOTLPTracingUrl(),
      propagateTraceHeaderCorsUrls,
    });

    this.isInitialized = true;
    return this.api;
  }

  updateWorkflowId(id: string) {
    this.api?.updateWorkflowId?.(id);
  }

  /**
   * Creates and starts a new span
   * @param spanName - Name of the span
   * @param attributes - Optional attributes to add to the span
   * @returns The created span
   */
  startSpan(spanName: string, attributes?: Record<string, any>): Span | undefined {
    try {
      this.currentSpan = createSpan(spanName);

      if (attributes && this.currentSpan) {
        this.currentSpan.setAttributes(attributes);
      }
      return this.currentSpan;
    } catch (error) {
      console.error('Failed to start span:', error);
      return undefined;
    }
  }

  /**
   * Ends the current span
   * @param attributes - Optional attributes to add before ending the span
   * @param error - Optional error to mark the span as failed
   */
  endSpan(attributes?: Record<string, any>, error?: Error): void {
    if (!this.currentSpan) {
      console.warn('No active span to end');
      return;
    }

    if (attributes) {
      this.currentSpan.setAttributes(attributes);
    }

    if (error) {
      this.currentSpan.recordException(error);
      this.currentSpan.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
    } else {
      this.currentSpan.setStatus({ code: SpanStatusCode.OK });
    }

    this.currentSpan.end();
    this.currentSpan = undefined;
  }

  /**
   * Adds an event to the current span
   * @param eventName - Name of the event
   * @param attributes - Optional attributes for the event
   */
  addEvent(eventName: string, attributes?: Record<string, any>): void {
    if (this.currentSpan) {
      this.currentSpan.addEvent(eventName, attributes);
    } else {
      console.warn('No active span to add event to');
    }
  }

  /**
   * Adds attributes to the current span
   * @param attributes - Attributes to add
   */
  addAttributes(attributes: Record<string, any>): void {
    if (this.currentSpan) {
      this.currentSpan.setAttributes(attributes);
    } else {
      console.warn('No active span to add attributes to');
    }
  }

  /**
   * Gets the current active span
   * @returns The current span or undefined
   */
  getCurrentSpan(): Span | undefined {
    return this.currentSpan;
  }

  getTracer() {
    return this.api?.getTracer?.();
  }

  getWorkflowId(): string | null {
    return this.api?.getWorkflowId?.() || null;
  }

  private getOTLPTracingUrl(): string | undefined {
    const { tracingUrl } = _getUrls();
    const env = import.meta.env;
    return _getEnvVariable(window, env, 'OTLP_TRACING_URL', tracingUrl);
  }
}
/**
 * Singleton instance of TracingService for easy access throughout the application
 */
export const tracingService = TracingService.getInstance();

/**
 * Updates the workflow ID in the tracing service
 */
export function updateWorkflowId(id: string) {
  tracingService.updateWorkflowId?.(id);
}
